/**
 * Canonical checkout occasion choices and rental-type eligibility.
 *
 * Keep these slugs aligned with the renter-facing occasion catalog. Historical
 * slugs remain accepted where they are still represented by that catalog.
 */
export const CHECKOUT_OCCASION_CAPABILITY = "required-v1" as const;
export const CHECKOUT_OCCASION_FLAG_KEY = "checkout-occasion-required" as const;

export const OCCASION_TO_EVENT_TYPE_SLUG: Readonly<Record<string, string | null>> = {
  wedding: "private_wedding",
  prom: "private_gala",
  "special-event": "private_birthday",
  anniversary: "private_birthday",
  birthday: "private_birthday",
  "film-tv": "commercial_film",
  "photo-shoot": "commercial_tv_advertisement",
  exhibition: "commercial_exhibitions",
  "corporate-event": "business_event",
  "business-travel": "business_vip",
  leisure: "sightseeing",
  "airport-transfer": "business_vip",
  sightseeing: "sightseeing",
  "night-out": "sightseeing",
  "brand-activation": "commercial_tv_advertisement",
  other: null,
};

export const ALLOWED_OCCASIONS: ReadonlySet<string> = new Set(
  Object.keys(OCCASION_TO_EVENT_TYPE_SLUG),
);

const CHECKOUT_OCCASIONS_BY_RENTAL_TYPE: Readonly<
  Record<"chauffeured" | "event", readonly string[]>
> = {
  chauffeured: [
    "wedding",
    "prom",
    "special-event",
    "anniversary",
    "birthday",
    "corporate-event",
    "airport-transfer",
    "sightseeing",
    "other",
  ],
  event: [
    "wedding",
    "photo-shoot",
    "film-tv",
    "exhibition",
    "corporate-event",
    "special-event",
    "other",
  ],
};

export type CheckoutOccasionValidation =
  | { ok: true; normalizedOccasion: string | null }
  | {
      ok: false;
      code: "occasion_required" | "invalid_occasion" | "occasion_not_applicable";
      message: string;
      field: "occasion";
    };

/**
 * Validates occasion presence and eligibility for capable checkout clients.
 * Self-drive checkout has no occasion control; chauffeur and event checkout
 * require a choice from the corresponding canonical picker.
 */
export function validateCheckoutOccasion(
  rentalType: string | null | undefined,
  occasion: unknown,
): CheckoutOccasionValidation {
  const normalizedRentalType = (rentalType ?? "").trim().toLowerCase();
  const applies =
    normalizedRentalType === "chauffeured" || normalizedRentalType === "event";
  const normalizedOccasion =
    typeof occasion === "string" && occasion.trim() ? occasion.trim() : null;

  if (!applies) {
    if (normalizedOccasion !== null) {
      return {
        ok: false,
        code: "occasion_not_applicable",
        message: "occasion is only allowed for chauffeured or event bookings.",
        field: "occasion",
      };
    }
    return { ok: true, normalizedOccasion: null };
  }

  if (normalizedOccasion === null) {
    return {
      ok: false,
      code: "occasion_required",
      message: "occasion is required for chauffeured and event bookings.",
      field: "occasion",
    };
  }

  const allowed =
    CHECKOUT_OCCASIONS_BY_RENTAL_TYPE[
      normalizedRentalType as "chauffeured" | "event"
    ];
  if (!ALLOWED_OCCASIONS.has(normalizedOccasion) || !allowed.includes(normalizedOccasion)) {
    return {
      ok: false,
      code: "invalid_occasion",
      message: "occasion is not available for this rental type.",
      field: "occasion",
    };
  }
  return { ok: true, normalizedOccasion };
}